import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';

import { C, DEMO_PATIENTS, STAGES } from '../../components/echo/frontdesk/frontDeskData';
import { FDTopBar } from '../../components/echo/frontdesk/FDTopBar';
import { FDContextBar } from '../../components/echo/frontdesk/FDContextBar';
import { FDSidebar } from '../../components/echo/frontdesk/FDSidebar';
import { FDBookingReview } from '../../components/echo/frontdesk/FDBookingReview';
import { OverviewView } from '../../components/echo/frontdesk/OverviewView';
import { PatientDetailView } from '../../components/echo/frontdesk/PatientDetailView';
import { PaymentPanel } from '../../components/echo/frontdesk/PaymentPanel';
import { CalendarView } from '../../components/echo/frontdesk/CalendarView';
import {
  X, DollarSign, Phone, ScanLine, Sparkles, CheckCircle2,
  ChevronDown, ChevronUp, Zap, AlertTriangle,
} from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

/* Punch list items for Front Desk slide-in panel */
const FD_PUNCH_LIST = [
  { id: 'fp-1', priority: 'red', patient: 'David Wilson (2pm)', issue: '42% no-show · no confirmation', financial: '~$200 at risk', action: 'Call' },
  { id: 'fp-2', priority: 'red', patient: 'Robert Johnson (10am)', issue: 'Insurance inactive', financial: 'Claim denial risk', action: 'Scan ID' },
  { id: 'fp-3', priority: 'amber', patient: '11:30am open slot', issue: '3 waitlist matches', financial: '$150 revenue', action: 'Auto-fill' },
  { id: 'fp-4', priority: 'amber', patient: 'Emily Davis (10:30am)', issue: '$20 copay uncollected', financial: 'Collection risk', action: 'Collect' },
  { id: 'fp-5', priority: 'green', patient: 'John Smith (9am)', issue: 'Confirmed · verified · checked in', financial: null, action: null },
];

const PRIORITY_COLORS = { red: C.red, amber: C.amber, blue: '#3b82f6', green: C.green };

const EchoFrontDesk = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patients, setPatients] = useState(DEMO_PATIENTS);
  const [view, setView] = useState('overview'); // overview | patient | calendar
  const [activeStage, setActiveStage] = useState('scheduled');
  const [activePatientId, setActivePatientId] = useState(null);
  const [showContextBar, setShowContextBar] = useState(true);
  const [showTools, setShowTools] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentDone, setPaymentDone] = useState(false);
  const [calView, setCalView] = useState('day');
  const [fadeIn, setFadeIn] = useState(false);
  const [sidebarW, setSidebarW] = useState(240);
  const [isResizing, setIsResizing] = useState(false);
  const [showPunchList, setShowPunchList] = useState(false);
  const [punchExpanded, setPunchExpanded] = useState(true);
  const [punchItems, setPunchItems] = useState(FD_PUNCH_LIST);

  const initials = (user?.name || 'FD').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const activePatient = patients.find(p => p.id === activePatientId);
  const stageCounts = STAGES.reduce((a, s) => { a[s.key] = patients.filter(p => p.stage === s.key).length; return a; }, {});
  const queuePatients = patients.filter(p => p.stage === activeStage);

  useEffect(() => { const t = setTimeout(() => setFadeIn(true), 50); return () => clearTimeout(t); }, []);

  const handleResize = useCallback((e) => {
    e.preventDefault(); setIsResizing(true);
    const startX = e.clientX, startW = sidebarW;
    const onMove = (ev) => setSidebarW(Math.min(360, Math.max(220, startW + ev.clientX - startX)));
    const onUp = () => { setIsResizing(false); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, [sidebarW]);

  const selectPatient = (id) => { setActivePatientId(id); setView('patient'); setShowPayment(false); setPaymentDone(false); };
  const goOverview = () => { setView('overview'); setActivePatientId(null); setShowPayment(false); };
  const goCalendar = () => { setView('calendar'); setActivePatientId(null); setShowPayment(false); };

  const advanceStage = (pid) => {
    const order = STAGES.map(s => s.key);
    setPatients(prev => prev.map(p => {
      if (p.id !== pid) return p;
      const idx = order.indexOf(p.stage);
      if (idx < order.length - 1) {
        const next = order[idx + 1];
        toast.success(`${p.name} → ${STAGES[idx + 1].label}`);
        return { ...p, stage: next, room: next === 'arrived' || next === 'with_provider' ? `Room ${Math.floor(Math.random() * 5) + 1}` : p.room };
      }
      return p;
    }));
  };

  const openPayment = (pid) => { setActivePatientId(pid); setView('patient'); setShowPayment(true); setPaymentDone(false); setPaymentMethod('card'); };

  const collectPayment = (pid) => {
    setPatients(prev => prev.map(p => p.id === pid ? { ...p, copay: { ...p.copay, collected: true } } : p));
    setPaymentDone(true);
    toast.success('Payment collected');
  };

  return (
    <div className={cn('fixed inset-0 z-40 flex flex-col bg-slate-50 transition-opacity duration-300', fadeIn ? 'opacity-100' : 'opacity-0')} data-testid="front-desk-mode">

      <FDTopBar initials={initials} showTools={showTools} setShowTools={setShowTools}
        goOverview={goOverview} onExit={() => { setFadeIn(false); setTimeout(() => navigate('/echo'), 300); }} navigate={navigate}
        onTogglePunchList={() => setShowPunchList(p => !p)}
        punchListCount={FD_PUNCH_LIST.filter(i => i.priority === 'red' || i.priority === 'amber').length} />

      <FDContextBar show={showContextBar} onClose={() => setShowContextBar(false)} />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <FDSidebar sidebarW={sidebarW} activeStage={activeStage} setActiveStage={setActiveStage}
          stageCounts={stageCounts} queuePatients={queuePatients} activePatientId={activePatientId}
          view={view} selectPatient={selectPatient} navigate={navigate} />

        {/* Resize handle */}
        <div onMouseDown={handleResize}
          style={{ width: 3, cursor: 'col-resize', background: isResizing ? '#e0e0e0' : 'transparent', transition: 'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e8e8e8'}
          onMouseLeave={e => { if (!isResizing) e.currentTarget.style.background = 'transparent'; }} />

        <div style={{ flex: 1, overflowY: 'auto', background: '#fafafa' }}>
          {/* ═══ INLINE AI PUNCH LIST BANNER ═══ */}
          {view === 'overview' && (
            <div style={{ margin: '12px 16px 0', borderRadius: 12, border: '1px solid #ebebeb', background: '#fff', overflow: 'hidden' }} data-testid="fd-punch-inline">
              <button onClick={() => setPunchExpanded(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Zap style={{ width: 13, height: 13, color: C.purple }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.black }}>AI Punch List</span>
                  {punchItems.filter(i => i.priority === 'red').length > 0 && (
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: C.red, color: '#fff', fontWeight: 600 }}>
                      {punchItems.filter(i => i.priority === 'red').length} urgent
                    </span>
                  )}
                  {punchItems.filter(i => i.priority === 'amber').length > 0 && (
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: C.amber, color: '#fff', fontWeight: 600 }}>
                      {punchItems.filter(i => i.priority === 'amber').length} action
                    </span>
                  )}
                </div>
                {punchExpanded ? <ChevronUp style={{ width: 14, height: 14, color: '#aaa' }} /> : <ChevronDown style={{ width: 14, height: 14, color: '#aaa' }} />}
              </button>
              {punchExpanded && (
                <div style={{ borderTop: '1px solid #f0f0f0' }}>
                  {punchItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '1px solid #f8f8f8' }} data-testid={`fd-inline-punch-${item.id}`}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: PRIORITY_COLORS[item.priority], flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 500, color: C.black }}>{item.patient}</span>
                        <span style={{ fontSize: 10, color: '#999', marginLeft: 6 }}>{item.issue}</span>
                      </div>
                      {item.financial && (
                        <span style={{ fontSize: 9, color: item.priority === 'red' ? C.red : C.amber, fontWeight: 600, whiteSpace: 'nowrap' }}>{item.financial}</span>
                      )}
                      {item.action && (
                        <button onClick={() => toast.info(`${item.action} action`)}
                          style={{ fontSize: 9, fontWeight: 600, padding: '3px 10px', borderRadius: 5, background: item.priority === 'amber' && item.action === 'Auto-fill' ? C.purple : C.black, color: '#fff', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                          data-testid={`fd-inline-action-${item.id}`}>
                          {item.action}
                        </button>
                      )}
                      {item.priority === 'green' && !item.action && <CheckCircle2 style={{ width: 13, height: 13, color: C.green, flexShrink: 0 }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === 'overview' && <FDBookingReview />}
          {view === 'overview' && <OverviewView patients={patients} goCalendar={goCalendar}
            selectPatient={selectPatient} openPayment={openPayment} advanceStage={advanceStage} />}          {view === 'patient' && activePatient && (
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <PatientDetailView patient={activePatient} goOverview={goOverview}
                  advanceStage={advanceStage} openPayment={openPayment} navigate={navigate} />
              </div>
              {showPayment && (
                <PaymentPanel patient={activePatient} method={paymentMethod} setMethod={setPaymentMethod}
                  done={paymentDone} onCollect={() => collectPayment(activePatient.id)}
                  onClose={() => setShowPayment(false)} />
              )}
            </div>
          )}
          {view === 'calendar' && <CalendarView goOverview={goOverview} calView={calView} setCalView={setCalView}
            patients={patients} selectPatient={selectPatient} />}
        </div>
      </div>

      {/* ═══ PUNCH LIST SLIDE-IN PANEL ═══ */}
      {showPunchList && (
        <div style={{ position: 'fixed', top: 44, right: 0, width: 360, height: 'calc(100vh - 44px)', background: '#fff', borderLeft: '1px solid #ebebeb', zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 16px rgba(0,0,0,0.06)', animation: 'slideInRight 0.2s ease' }} data-testid="fd-punch-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles style={{ width: 14, height: 14, color: C.purple }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.black }}>Today's Punch List</span>
            </div>
            <button onClick={() => setShowPunchList(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 2 }} data-testid="fd-punch-close">
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {FD_PUNCH_LIST.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderBottom: '1px solid #f5f5f5' }} data-testid={`fd-punch-${item.id}`}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_COLORS[item.priority], flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{item.patient}</p>
                  <p style={{ fontSize: 10, color: '#888' }}>{item.issue}</p>
                  {item.financial && <p style={{ fontSize: 9, color: item.priority === 'red' ? C.red : C.amber, fontWeight: 500 }}>{item.financial}</p>}
                </div>
                {item.action && (
                  <button onClick={() => toast.info(`${item.action} action`)}
                    style={{ fontSize: 10, fontWeight: 500, padding: '3px 10px', borderRadius: 5, background: C.black, color: '#fff', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                    data-testid={`fd-punch-action-${item.id}`}>{item.action}</button>
                )}
                {item.priority === 'green' && <CheckCircle2 style={{ width: 14, height: 14, color: C.green, flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes slideInRight { from { transform: translateX(360px); } to { transform: translateX(0); } }
      `}</style>
      <Toaster position="bottom-right" />
    </div>
  );
};

export default EchoFrontDesk;
